import { useSortable, SortableTh } from "@/components/common/SortableTable";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, fmtNumber } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Wrench, AlertCircle, Clock, PlayCircle, CheckCircle2, Activity,
  RefreshCw, Search, ShieldAlert, DatabaseZap, Play, RotateCcw, Save,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { ProvenanceButton } from "@/components/common/ProvenanceButton";
import { AIAgentPanel } from "@/components/ai/AIAgentPanel";
import { AroFloSyncPanel } from "@/components/operations/AroFloSyncPanel";
import { AgedWorkPanel } from "@/components/operations/AgedWorkPanel";
import { WipPanel } from "@/components/operations/WipPanel";

// Coerce any Aroflo/Mongo value to a string safely — some upstream sources
// return nested objects (e.g. status = {code: "OPEN", label: "Open"}) which
// would otherwise crash React with "Objects are not valid as a React child"
// and blank out the whole Jobs tab.
function safeStr(v, fallback = "—") {
  if (v == null) return fallback;
  if (typeof v === "string") return v || fallback;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    // Common shapes: {name}, {label}, {code}, {value}, {$oid: "..."}
    return v.name ?? v.label ?? v.code ?? v.value ?? v.$oid ?? JSON.stringify(v).slice(0, 40);
  }
  return String(v);
}

function safeDate(v) {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === "object" && v.$date) return String(v.$date).slice(0, 10);
  return "";
}


function KpiCard({ enterpriseId, label, value, sub, icon: Icon, tone = "default", testid }) {
  const border = tone === "warn"    ? "border-amber-200 bg-amber-50/40"
              : tone === "good"     ? "border-emerald-200 bg-emerald-50/40"
              : tone === "danger"   ? "border-rose-200 bg-rose-50/40"
              :                       "border-slate-200 bg-white";
  return (
    <div data-testid={testid} className={`rounded-lg border ${border} p-4 relative`}>
      <div className="absolute top-2 right-2"><ProvenanceButton enterpriseId={enterpriseId} size="xs" /></div>
      <div className="flex items-center gap-2 text-slate-500">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-[10px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-display font-semibold text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}


export default function OperationsPage() {
  const { can, loaded, role } = usePermissions();
  const showAroFloSync = role !== "admin" && role !== "finance_admin";
  const [summary, setSummary] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [trends, setTrends] = useState(null);
  const [top, setTop] = useState([]);
  const [topMeta, setTopMeta] = useState({ fallback_used: false, window: "last_90_days" });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("__all");
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    // Partial-failure tolerant — one slow endpoint shouldn't zero the whole page.
    const results = await Promise.allSettled([
      api.get("/operations/summary"),
      api.get("/operations/trends?days=30"),
      api.get("/operations/top-clients?limit=8"),
    ]);
    const [s, t, tc] = results;
    if (s.status === "fulfilled") setSummary(s.value.data);
    if (t.status === "fulfilled") setTrends(t.value.data);
    if (tc.status === "fulfilled") {
      setTop(tc.value.data.items || []);
      setTopMeta({ fallback_used: !!tc.value.data.fallback_used, window: tc.value.data.window || "last_90_days" });
    }
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length) {
      const first = failed[0].reason;
      toast.error(`${failed.length} of 3 Operations endpoints failed. ${first?.response?.data?.detail || first?.message || ""}`);
    }
    setLoading(false);
  };

  // Jobs load server-side so search/filter spans ALL jobs (not just a loaded
  // window). Debounced; when no filter is active we fetch a modest page, and
  // when searching/filtering we pull the full matching set (capped at 1000).
  const jobsTimer = useRef(null);
  useEffect(() => {
    if (!loaded) return;
    if (jobsTimer.current) clearTimeout(jobsTimer.current);
    const active = (q && q.trim()) || statusFilter !== "__all";
    jobsTimer.current = setTimeout(() => {
      const params = new URLSearchParams({ limit: active ? "1000" : "200" });
      if (q && q.trim()) params.set("q", q.trim());
      if (statusFilter !== "__all") params.set("status", statusFilter);
      api.get(`/operations/jobs?${params.toString()}`)
        .then((r) => setJobs(r.data.items || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(jobsTimer.current);
  }, [q, statusFilter, loaded]);

  useEffect(() => {
    // Operations can be viewed by anyone above field worker; check gently
    if (loaded) load();
    // eslint-disable-next-line
  }, [loaded]);

  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      const statusStr = safeStr(j.status, "").toLowerCase();
      if (statusFilter !== "__all" && !statusStr.includes(statusFilter)) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      const haystack = [j.title, j.name, j.job_number, j.client_name]
        .map((v) => safeStr(v, "")).join(" ").toLowerCase();
      return haystack.includes(s);
    });
  }, [jobs, q, statusFilter]);
  const { sorted: sortedJobs, sort: jobSort, setSort: setJobSort } = useSortable(filtered);
  const { sorted: sortedTop, sort: topSort, setSort: setTopSort } = useSortable(top);

  if (!loaded) return <AppLayout title="Operations"><div className="h-40 shimmer" /></AppLayout>;

  return (
    <AppLayout
      title="Operations"
      subtitle="Ver1 module — 45 KPIs · Jobs · Backlog · PPM · Productivity"
      actions={
        <div className="flex items-center gap-2">
          <AIAgentPanel
            agentId="operations"
            agentName="Operations Agent"
            buttonLabel="Ask Operations Agent"
            buttonVariant="filled"
            testid="ops-ai-agent-btn"
          />
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="ops-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      }
    >
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        <KpiCard enterpriseId="OPS-KPI-001" label="Open jobs"       value={fmtNumber(summary?.open_jobs ?? 0)}       icon={Wrench}       testid="ops-kpi-open" />
        <KpiCard enterpriseId="OPS-KPI-002" label="Overdue"         value={fmtNumber(summary?.overdue_jobs ?? 0)}    icon={AlertCircle}  testid="ops-kpi-overdue"   tone={summary?.overdue_jobs > 0 ? "danger" : "default"} />
        <KpiCard enterpriseId="OPS-KPI-003" label="Work in progress" value={fmtNumber(summary?.wip_jobs ?? 0)}       icon={PlayCircle}   testid="ops-kpi-wip" />
        <KpiCard enterpriseId="OPS-KPI-004" label="Completed 7d"    value={fmtNumber(summary?.completed_7d ?? 0)}   icon={CheckCircle2} testid="ops-kpi-c7"   tone="good" />
        <KpiCard enterpriseId="OPS-KPI-006" label="Reactive %"      value={`${summary?.reactive_pct_30d ?? 0}%`}    sub="Last 30 days" icon={Activity}    testid="ops-kpi-reactive"  tone={(summary?.reactive_pct_30d ?? 0) > 40 ? "warn" : "default"} />
        <KpiCard enterpriseId="OPS-KPI-008" label="Backlog days"    value={fmtNumber(summary?.backlog_days_median ?? 0)} sub="Median" icon={Clock}    testid="ops-kpi-backlog"   tone={(summary?.backlog_days_median ?? 0) > 30 ? "warn" : "default"} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto flex-wrap gap-1 justify-start">
          <TabsTrigger data-testid="ops-tab-overview" value="overview" className="text-xs data-[state=active]:bg-white">Overview</TabsTrigger>
          <TabsTrigger data-testid="ops-tab-jobs" value="jobs" className="text-xs data-[state=active]:bg-white">Jobs</TabsTrigger>
          <TabsTrigger data-testid="ops-tab-wip" value="wip" className="text-xs data-[state=active]:bg-white">WIP</TabsTrigger>
          <TabsTrigger data-testid="ops-tab-trends" value="trends" className="text-xs data-[state=active]:bg-white">Trends</TabsTrigger>
          <TabsTrigger data-testid="ops-tab-clients" value="clients" className="text-xs data-[state=active]:bg-white">Top clients</TabsTrigger>
          <TabsTrigger data-testid="ops-tab-aged" value="aged" className="text-xs data-[state=active]:bg-white">Aged work</TabsTrigger>
          {showAroFloSync && (
            <TabsTrigger data-testid="ops-tab-sync" value="sync" className="text-xs data-[state=active]:bg-white">Aroflo Sync</TabsTrigger>
          )}
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-3">
                <Activity className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-wider font-medium">Created vs completed (30 days)</span>
                <ProvenanceButton enterpriseId="OPS-KPI-020" size="xs" className="ml-auto" />
              </div>
              <div className="h-48 min-h-[192px]">
                {(() => {
                  const hasData = trends?.series?.some?.((d) => (d.created || 0) + (d.completed || 0) > 0);
                  if (!trends?.series) return <div className="h-full flex items-center justify-center text-xs text-slate-400">Loading trend…</div>;
                  if (!hasData) return <div className="h-full flex items-center justify-center text-xs text-slate-400 text-center px-6">No job creation/completion events in the last 30 days. Run the Aroflo sync to pull recent activity.</div>;
                  return (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={trends.series}>
                        <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={4} tickMargin={4} />
                        <YAxis tick={{ fontSize: 9 }} width={30} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        <Bar dataKey="created"   name="Created"   fill="#0f766e" />
                        <Bar dataKey="completed" name="Completed" fill="#65a30d" />
                      </BarChart>
                    </ResponsiveContainer>
                  );
                })()}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-3">
                <Wrench className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-wider font-medium">PPM completion</span>
                <ProvenanceButton enterpriseId="OPS-KPI-010" size="xs" className="ml-auto" />
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span data-testid="ops-ppm-completion-pct" className="text-5xl font-display font-semibold text-emerald-700">{summary?.ppm_completion_pct_30d ?? 0}%</span>
                <span className="text-xs text-slate-500">
                  {summary?.ppm_completed ?? 0} of {summary?.ppm_total ?? 0} PPM jobs completed
                  {summary?.ppm_window === "all_time" ? " (all time)" : " (due last 90 days)"}
                </span>
              </div>
              <div className="mt-3 text-xs text-slate-600 leading-relaxed">
                Percentage of preventive-maintenance (PPM) jobs due in the window that have actually been completed or closed in Aroflo. A healthy ops team runs at ≥ 90%.
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Jobs list */}
        <TabsContent value="jobs" className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-[280px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="ops-search" value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search jobs…" className="pl-9 h-9 bg-white" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="ops-status-filter" className="w-[220px] h-9 bg-white"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in-progress">In progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto text-xs text-slate-500">{filtered.length} of {jobs.length} jobs</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {filtered.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center">No jobs match the current filters.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <SortableTh label="Job #" k="job_number" sort={jobSort} setSort={setJobSort} />
                      <SortableTh label="Title" k="title" sort={jobSort} setSort={setJobSort} />
                      <SortableTh label="Client" k="client_name" sort={jobSort} setSort={setJobSort} />
                      <SortableTh label="Status" k="status" sort={jobSort} setSort={setJobSort} />
                      <SortableTh label="Type" k="task_type" sort={jobSort} setSort={setJobSort} />
                      <SortableTh label="Created" k="created_at" sort={jobSort} setSort={setJobSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedJobs.slice(0, 250).map((j) => (
                      <tr key={j.id} data-testid={`ops-job-row-${j.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-mono text-[10px] text-slate-600">{safeStr(j.job_number)}</td>
                        <td className="px-3 py-2 text-slate-800 max-w-md truncate">{safeStr(j.title || j.name)}</td>
                        <td className="px-3 py-2 text-slate-700">{safeStr(j.client_name)}</td>
                        <td className="px-3 py-2">
                          <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">{safeStr(j.status, "unknown")}</Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">{safeStr(j.task_type)}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{safeDate(j.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Trends */}
        <TabsContent value="trends" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-3">
              <Activity className="w-3.5 h-3.5" />
              <span className="text-[10px] uppercase tracking-wider font-medium">Job flow — created vs completed</span>
              {trends?.fallback_used && (
                <span className="text-[10px] font-normal text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                  Showing archive · window ends {trends.window_end}
                </span>
              )}
              <ProvenanceButton enterpriseId="OPS-KPI-020" size="xs" className="ml-auto" />
            </div>
            <div className="h-72 min-h-[288px]">
              {(() => {
                const hasData = trends?.series?.some?.((d) => (d.created || 0) + (d.completed || 0) > 0);
                if (!trends?.series) return <div className="h-full flex items-center justify-center text-xs text-slate-400">Loading trend…</div>;
                if (!hasData) return <div className="h-full flex items-center justify-center text-xs text-slate-400 text-center px-6">No job creation/completion events in the last 30 days. Run the Aroflo sync to pull recent activity.</div>;
                return (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trends.series}>
                      <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={2} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="created"   name="Created"   fill="#0f766e" />
                      <Bar dataKey="completed" name="Completed" fill="#65a30d" />
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </div>
          </div>
        </TabsContent>

        {/* Top clients */}
        <TabsContent value="clients" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">
                Top clients by job volume {topMeta.fallback_used ? "(all-time archive)" : "(last 90 days)"}
              </span>
              {topMeta.fallback_used && (
                <span className="text-[10px] font-normal text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                  No jobs in last 90 days
                </span>
              )}
              <ProvenanceButton enterpriseId="OPS-KPI-035" size="xs" className="ml-auto" />
            </div>
            {top.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center">No data yet — jobs synced from Aroflo will populate this table.</div>
            ) : (
              <table className="w-full text-sm" data-testid="ops-top-clients-table">
                <thead>
                  <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <SortableTh label="Client" k="client" sort={topSort} setSort={setTopSort} className="text-left font-medium px-3 py-2" />
                    <SortableTh label="Jobs" k="jobs" sort={topSort} setSort={setTopSort} className="text-right font-medium px-3 py-2" />
                    <SortableTh label="Reactive" k="reactive" sort={topSort} setSort={setTopSort} className="text-right font-medium px-3 py-2" />
                    <SortableTh label="PPM" k="ppm" sort={topSort} setSort={setTopSort} className="text-right font-medium px-3 py-2" />
                    <SortableTh label="Other" k="other" sort={topSort} setSort={setTopSort} className="text-right font-medium px-3 py-2" />
                    <th className="text-right font-medium px-3 py-2">Reactive %</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTop.map((r) => (
                    <tr key={r.client} className="border-b border-slate-100 hover:bg-slate-50/60">
                      <td className="px-3 py-2 text-slate-800 font-medium">{r.client}</td>
                      <td className="px-3 py-2 text-right">{r.jobs}</td>
                      <td className="px-3 py-2 text-right text-rose-700">{r.reactive}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{r.ppm}</td>
                      <td className="px-3 py-2 text-right text-slate-500">{r.other ?? Math.max(0, r.jobs - r.reactive - r.ppm)}</td>
                      <td className="px-3 py-2 text-right">{r.jobs > 0 ? Math.round((r.reactive / r.jobs) * 100) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* Aged work exceptions */}
        <TabsContent value="wip" className="mt-4">
          {tab === "wip" && <WipPanel />}
        </TabsContent>

        <TabsContent value="aged" className="mt-4">
          <AgedWorkPanel />
        </TabsContent>

        {/* Aroflo Sync */}
        {showAroFloSync && (
          <TabsContent value="sync" className="mt-4">
            <AroFloSyncPanel />
          </TabsContent>
        )}
      </Tabs>
    </AppLayout>
  );
}